import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, UserPlus, Users, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = { accompagnateur: 'Accompagnateur', coordinateur: 'Coordinateur' };

export default function AdrnsMembres() {
    const { session } = useAuth();
    const [data, setData] = useState(null);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('accompagnateur');
    const [saving, setSaving] = useState(false);
    const hdrs = () => ({ headers: { Authorization: `Bearer ${session.access_token}` } });

    const reload = () => axios.get(`${API}/adrns/members`, hdrs()).then(r => setData(r.data)).catch(() => setData({ items: [], can_manage: false }));
    useEffect(() => { if (session) reload(); /* eslint-disable-next-line */ }, [session]);

    const add = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/adrns/members`, { email, role }, hdrs());
            toast.success('Membre ADRNS ajouté');
            setEmail('');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setSaving(false);
    };

    const patchMember = async (uid, updates) => {
        try {
            await axios.patch(`${API}/adrns/members/${uid}`, updates, hdrs());
            toast.success('Membre mis à jour');
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const removeMember = async (m) => {
        if (!window.confirm(`Retirer définitivement ${m.email} des membres ADRNS ?\nIl sera aussi désassigné de tous les dossiers.`)) return;
        try {
            const r = await axios.delete(`${API}/adrns/members/${m.user_id}`, hdrs());
            toast.success(`Membre retiré${r.data.dossiers_desassignes ? ` · désassigné de ${r.data.dossiers_desassignes} dossier(s)` : ''}`);
            reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-6" data-testid="adrns-membres-page">
            <div>
                <div className="eyebrow mb-2">ADRNS · Équipe</div>
                <h1 className="font-heading text-4xl">Membres ADRNS</h1>
                <p className="text-sm text-foreground/60 mt-2">Les membres habilités accèdent à l'Espace ADRNS et voient les dossiers qui leur sont assignés. Les coordinateurs voient tous les dossiers.</p>
            </div>

            {data.can_manage && (
                <div className="card-soft p-5 flex flex-wrap items-center gap-3" data-testid="adrns-add-member-form">
                    <Input placeholder="Email d'un compte existant" type="email" value={email}
                        onChange={e => setEmail(e.target.value)} className="flex-1 min-w-[220px]" data-testid="adrns-member-email" />
                    <select value={role} onChange={e => setRole(e.target.value)}
                        className="h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="adrns-member-role">
                        {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <Button onClick={add} disabled={saving || !email.includes('@')} data-testid="adrns-member-add-btn">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <><UserPlus size={14} className="mr-1.5" /> Habiliter</>}
                    </Button>
                </div>
            )}

            {data.items.length === 0 ? (
                <div className="card-soft p-12 text-center text-foreground/60" data-testid="adrns-members-empty">
                    <Users size={30} className="mx-auto mb-3 text-muted-foreground" />
                    Aucun membre habilité. {data.can_manage ? 'Ajoutez le premier membre ci-dessus.' : 'Le Super Admin habilite les membres.'}
                </div>
            ) : (
                <div className="card-soft divide-y divide-border">
                    {data.items.map(m => (
                        <div key={m.user_id} className="flex items-center gap-4 p-4" data-testid={`adrns-member-${m.user_id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{m.full_name || m.email}</div>
                                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                            </div>
                            {data.can_manage ? (
                                <select value={m.role} onChange={e => patchMember(m.user_id, { role: e.target.value })}
                                    className="h-8 px-2 rounded-md border border-input bg-background text-xs">
                                    {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                </select>
                            ) : (
                                <span className="text-xs uppercase tracking-wider text-muted-foreground">{ROLES[m.role] || m.role}</span>
                            )}
                            {data.can_manage && (
                                <div className="flex items-center gap-1.5">
                                    <Switch checked={!!m.active} onCheckedChange={v => patchMember(m.user_id, { active: v })} data-testid={`adrns-member-active-${m.user_id}`} />
                                    <span className="text-xs text-muted-foreground w-12">{m.active ? 'Actif' : 'Inactif'}</span>
                                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive h-8 w-8 p-0"
                                        onClick={() => removeMember(m)} aria-label="Retirer le membre"
                                        data-testid={`adrns-member-delete-${m.user_id}`}>
                                        <Trash2 size={14} />
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
